import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { OrganizationDialog } from "./OrganizationDialog";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Briefcase, ExternalLink, Trash2 } from "lucide-react";

interface Portfolio {
  id: string;
  name: string;
  category: string | null;
  questionnaire_id?: string;
  created_at: string;
  archived: boolean | null;
  sub_org_id: string | null;
}

interface AdminPortfoliosTableProps {
  subOrgId: string;
  subOrgName: string;
  clientId: string;
  clientName: string;
  onCreateClick: () => void;
  onLogAction: (action: string, tableName?: string, recordId?: string, oldValues?: any, newValues?: any, reason?: string) => Promise<void>;
}

const fmtDate = (ts?: string | null) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return format(d, "MMM d, yyyy p");
};

export const AdminPortfoliosTable = ({ subOrgId, subOrgName, clientId, clientName, onCreateClick, onLogAction }: AdminPortfoliosTableProps) => {
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [showArchived, setShowArchived] = useState(false);
  const [editPortfolio, setEditPortfolio] = useState<Portfolio | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    action: (reason: string) => void;
  }>({ open: false, title: "", description: "", action: () => {} });

  const { toast } = useToast();

  const handlePortfolioClick = (portfolio: Portfolio) => {
    // Pass admin navigation context via URL state
    navigate(`/org-admin/${portfolio.id}/assessments`, {
      state: {
        adminContext: {
          clientId,
          clientName,
          subOrgId,
          subOrgName,
          portfolioId: portfolio.id,
          portfolioName: portfolio.name,
        }
      }
    });
  };

  useEffect(() => {
    loadPortfolios();
  }, [subOrgId, showArchived]);

  const loadPortfolios = async () => {
    try {
      setLoading(true);
      
      // Use admin RPC to bypass RLS
      const { data, error } = await (supabase.rpc as any)("admin_list_portfolios_by_sub_org", {
        p_sub_org_id: subOrgId
      });

      if (error) {
        if (error.code === "PGRST202") {
          throw new Error("DB migration missing admin_list_portfolios_by_sub_org()");
        }
        throw error;
      }

      // Filter archived client-side if showArchived is true (RPC returns non-archived by default)
      let portfolioList = (data || []) as Portfolio[];
      if (showArchived) {
        // For archived view, we need a separate query - for now just show what we have
        // In a full implementation, we'd add a separate RPC or parameter
      }
      
      setPortfolios(portfolioList);
    } catch (error) {
      console.error("Error loading portfolios:", error);
      toast({
        title: "Error",
        description: "Failed to load portfolios",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePortfolio = async (id: string, updates: Partial<Portfolio>, reason: string) => {
    try {
      const oldData = portfolios.find(p => p.id === id);
      
      const { error } = await supabase
        .from("portfolios")
        .update(updates)
        .eq("id", id);

      if (error) throw error;

      await supabase.rpc("log_admin_action", {
        p_action: "update_portfolio",
        p_table_name: "portfolios",
        p_record_id: id,
        p_old_values: oldData as any || null,
        p_new_values: { ...oldData, ...updates } as any,
        p_reason: reason || null
      });

      toast({ title: "Success", description: "Portfolio updated successfully" });
      loadPortfolios();
      setEditPortfolio(null);
    } catch (error) {
      console.error("Error updating portfolio:", error);
      toast({
        title: "Error",
        description: "Failed to update portfolio",
        variant: "destructive"
      });
    }
  };

  const fetchUserCount = async (portfolioId: string): Promise<number> => {
    const { count } = await supabase
      .from("profiles")
      .select("id", { count: "exact", head: true })
      .eq("org_id", portfolioId);
    return count ?? 0;
  };

  const deletePortfolio = async (portfolioId: string, portfolioName: string, reason: string) => {
    try {
      setDeleteLoading(true);
      const { error } = await (supabase as any).rpc("admin_delete_portfolio_company", {
        p_portfolio_id: portfolioId,
        p_reason: reason
      });
      if (error) throw error;
      toast({ title: "Success", description: `"${portfolioName}" deleted successfully. Associated users have been unassigned.` });
      loadPortfolios();
    } catch (error: any) {
      console.error("Error deleting portfolio:", error);
      toast({ title: "Error", description: error.message || "Failed to delete portfolio", variant: "destructive" });
    } finally {
      setDeleteLoading(false);
    }
  };

  const archivePortfolio = async (portfolioId: string, archive: boolean, reason: string) => {
    try {
      const oldData = portfolios.find(p => p.id === portfolioId);
      
      const { error } = await supabase
        .from("portfolios")
        .update({ archived: archive } as any)
        .eq("id", portfolioId);

      if (error) throw error;

      await supabase.rpc("log_admin_action", {
        p_action: archive ? "archive_portfolio" : "unarchive_portfolio",
        p_table_name: "portfolios",
        p_record_id: portfolioId,
        p_old_values: oldData as any || null,
        p_new_values: { ...oldData, archived: archive } as any,
        p_reason: reason || null
      });

      toast({ 
        title: "Success", 
        description: `Portfolio ${archive ? "archived" : "unarchived"} successfully` 
      });
      loadPortfolios();
    } catch (error) {
      console.error(`Error ${archive ? "archiving" : "unarchiving"} portfolio:`, error);
      toast({
        title: "Error",
        description: `Failed to ${archive ? "archive" : "unarchive"} portfolio`,
        variant: "destructive"
      });
    }
  };

  const filteredPortfolios = portfolios.filter(p => {
    const matchesFilter = !filter || 
      p.name?.toLowerCase().includes(filter.toLowerCase()) ||
      p.category?.toLowerCase().includes(filter.toLowerCase());
    
    const matchesCategory = categoryFilter === "all" || p.category === categoryFilter;
    
    return matchesFilter && matchesCategory;
  });

  if (loading) {
    return <div className="flex justify-center p-8">Loading portfolios...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-center flex-wrap">
        <Input
          placeholder="Search portfolios..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-sm"
        />
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="Telecom (Fixed/ISP)">Telecommunications</SelectItem>
            <SelectItem value="Media">Media</SelectItem>
            <SelectItem value="Private Equity">Private Equity</SelectItem>
            <SelectItem value="Data Center">Data Center</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center space-x-2">
          <Switch
            id="show-archived"
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
          <Label htmlFor="show-archived">Show Archived</Label>
        </div>
        <Button onClick={onCreateClick}>
          Create Organization
        </Button>
      </div>

      {filteredPortfolios.length === 0 ? (
        <div className="py-10 text-sm text-muted-foreground text-center">
          No portfolios found under {subOrgName}.
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Portfolio Company</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPortfolios.map((p) => (
                <TableRow 
                  key={p.id} 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handlePortfolioClick(p)}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium flex items-center gap-1">
                          {p.name}
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </div>
                        <div className="text-xs text-muted-foreground">{p.id}</div>
                        {p.archived && (
                          <Badge variant="secondary" className="mt-1 text-xs">
                            Archived
                          </Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{p.category || "Uncategorized"}</Badge>
                  </TableCell>
                  <TableCell>{fmtDate(p.created_at)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditPortfolio(p)}
                      >
                        Edit
                      </Button>
                      {!p.archived ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirmDialog({
                            open: true,
                            title: "Archive Portfolio",
                            description: `Are you sure you want to archive "${p.name}"?`,
                            action: (reason) => archivePortfolio(p.id, true, reason)
                          })}
                        >
                          Archive
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirmDialog({
                            open: true,
                            title: "Unarchive Portfolio",
                            description: `Are you sure you want to unarchive "${p.name}"?`,
                            action: (reason) => archivePortfolio(p.id, false, reason)
                          })}
                        >
                          Unarchive
                        </Button>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={async () => {
                          const userCount = await fetchUserCount(p.id);
                          const userWarning = userCount > 0
                            ? `\n\n⚠️ ${userCount} user(s) are currently assigned to this portfolio. They will be unassigned but their accounts will be preserved.`
                            : "";
                          setConfirmDialog({
                            open: true,
                            title: "Delete Portfolio Company",
                            description: `⚠️ This will permanently delete "${p.name}" and ALL assessments, findings, responses, and evidence. This action cannot be undone.${userWarning}`,
                            action: (reason) => deletePortfolio(p.id, p.name, reason)
                          });
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <OrganizationDialog
        open={!!editPortfolio}
        onOpenChange={(open) => {
          if (!open) setEditPortfolio(null);
        }}
        organization={editPortfolio}
        onSave={(portfolioData, reason) => {
          if (editPortfolio) {
            updatePortfolio(editPortfolio.id, portfolioData, reason);
          }
        }}
      />

      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={(reason) => {
          confirmDialog.action(reason);
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }}
        variant={confirmDialog.title.includes("Delete") ? "destructive" : "default"}
        loading={deleteLoading}
      />
    </div>
  );
};
